import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Link as LinkIcon, Trash2, Plus, ExternalLink, Clock } from "lucide-react";

const EXPIRY_OPTIONS = [
  { value: "0", label: "Never expires" },
  { value: "1", label: "1 day" },
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
];

export default function ShareDialog({ open, onOpenChange }) {
  const [links, setLinks] = useState([]);
  const [name, setName] = useState("");
  const [expiresIn, setExpiresIn] = useState("0");
  const [includeEmail, setIncludeEmail] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/share/links");
      setLinks(data);
    } catch (_) {}
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const create = async () => {
    setCreating(true);
    try {
      const body = {
        name: name || "Public Org Chart",
        expires_in_days: Number(expiresIn) || null,
        include_email: includeEmail,
      };
      await api.post("/share/links", body);
      setName("");
      setExpiresIn("0");
      setIncludeEmail(false);
      toast.success("Share link created");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
    setCreating(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Revoke this link? Anyone with it will lose access.")) return;
    try {
      await api.delete(`/share/links/${id}`);
      toast.success("Link revoked");
      await load();
    } catch {
      toast.error("Revoke failed");
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/share/${token}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Link copied"),
      () => toast.error("Copy failed"),
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: '"Work Sans", sans-serif' }}>Share read-only org chart</DialogTitle>
          <DialogDescription className="text-xs text-neutral-600">
            Anyone with the link can view a sanitised version of your organogram in their browser — no sign-in required. Revoke anytime.
          </DialogDescription>
        </DialogHeader>

        {/* Create form */}
        <div className="space-y-3 border border-neutral-200 p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Label</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Board pack — Feb 2026"
                className="rounded-none border-neutral-300"
                data-testid="share-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Expires after</Label>
              <Select value={expiresIn} onValueChange={setExpiresIn}>
                <SelectTrigger className="rounded-none border-neutral-300" data-testid="share-expiry-select">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  {EXPIRY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-neutral-100 pt-3">
            <div>
              <p className="text-sm font-medium">Include emails</p>
              <p className="text-[11px] text-neutral-600">If OFF, employee emails are stripped from the public view.</p>
            </div>
            <Switch checked={includeEmail} onCheckedChange={setIncludeEmail} data-testid="share-include-email-toggle"/>
          </div>
          <Button onClick={create} disabled={creating} className="rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]" data-testid="create-share-link-button">
            <Plus size={14} className="mr-2"/>
            {creating ? "Creating…" : "Create new link"}
          </Button>
        </div>

        {/* Existing links */}
        <div className="border border-neutral-200">
          {links.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-neutral-600">No share links yet.</p>
          ) : (
            <ul>
              {links.map((l) => {
                const url = `${window.location.origin}/share/${l.token}`;
                const expired = l.expires_at && new Date(l.expires_at) < new Date();
                return (
                  <li key={l.id} className="space-y-2 border-b border-neutral-100 px-4 py-3 last:border-0" data-testid={`share-row-${l.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <LinkIcon size={14} className="text-[#002FA7]"/>
                        <p className="truncate text-sm font-medium">{l.name}</p>
                        {l.include_email && <Badge variant="outline" className="rounded-none border-neutral-300 text-[9px] uppercase">+email</Badge>}
                        {expired && <Badge variant="outline" className="rounded-none border-[#C50F1F] text-[9px] uppercase text-[#C50F1F]">Expired</Badge>}
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => copyLink(l.token)} title="Copy link" className="p-1.5 text-neutral-500 hover:text-black" data-testid={`copy-share-${l.id}`}>
                          <Copy size={14}/>
                        </button>
                        <a href={url} target="_blank" rel="noopener noreferrer" title="Open" className="p-1.5 text-neutral-500 hover:text-black">
                          <ExternalLink size={14}/>
                        </a>
                        <button onClick={() => remove(l.id)} title="Revoke" className="p-1.5 text-neutral-500 hover:text-[#C50F1F]" data-testid={`revoke-share-${l.id}`}>
                          <Trash2 size={14}/>
                        </button>
                      </div>
                    </div>
                    <p className="truncate font-mono text-[10px] text-neutral-500">{url}</p>
                    <p className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                      <Clock size={10}/>
                      {l.expires_at ? `Expires ${new Date(l.expires_at).toLocaleDateString()}` : "Never expires"}
                      <span>·</span>
                      <span>by {l.created_by}</span>
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
